import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  finalize,
  first,
  tap,
} from 'rxjs';
import { InventoryItemGeneralInfoUpdate } from 'src/app/core/models/inventory/inventory-item-general-info-update.interface';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { InventoryItemOverviewFormModel } from 'src/app/core/models/inventory/inventory-item-overview-form.model';
import {
  InventoryItemOverviewEditableFieldName,
  InventoryItemOverviewField,
} from 'src/app/core/models/inventory/inventory-item-overview-view-model.interface';
import { CoreModule } from 'src/app/core/core.module';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { isConcurrencyConflictError } from 'src/app/core/utils/api-error.util';

type InventoryItemDescriptionEditableFieldName = Extract<
  InventoryItemOverviewEditableFieldName,
  'shortDescription' | 'fullDescription'
>;

type InventoryItemDescriptionPreviewUpdater = (
  item: InventoryItem,
  value: string,
) => Omit<InventoryItemGeneralInfoUpdate, 'expectedConcurrencyToken'>;

interface InventoryItemDescriptionViewModel {
  previewFieldLabelKey?: string;
  fields: Array<InventoryItemOverviewField & { controlName: InventoryItemDescriptionEditableFieldName }>;
}

@Component({
  selector: 'app-inventory-item-description-tab',
  templateUrl: './inventory-item-description-tab.component.html',
  styleUrl: './inventory-item-description-tab.component.scss',
  imports: [CommonModule, ReactiveFormsModule, CoreModule],
  providers: [InventoryItemOverviewFormModel],
  standalone: true,
})
export class InventoryItemDescriptionTabComponent implements OnChanges {
  readonly overviewForm = inject(InventoryItemOverviewFormModel);
  private readonly inventoryItemService = inject(InventoryItemService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public itemId = '';
  @Input() public reloadToken = '';
  @Input() public inventoryItem: InventoryItem | null = null;
  @Output() public readonly inventoryItemChange = new EventEmitter<InventoryItem>();
  @Output() public readonly concurrencyConflict = new EventEmitter<void>();

  public readonly inventoryItem$ = new BehaviorSubject<InventoryItem | null>(null);
  public readonly viewModel$ = new BehaviorSubject<InventoryItemDescriptionViewModel | null>(null);
  public isLoading = false;

  private readonly patchUpdaters: Record<
    InventoryItemDescriptionEditableFieldName,
    InventoryItemDescriptionPreviewUpdater
  > = {
    shortDescription: (_, value) => ({ shortDescription: this.toOptionalString(value) }),
    fullDescription: (_, value) => ({ fullDescription: this.toOptionalString(value) }),
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['companyId'] && !changes['itemId'] && !changes['reloadToken'] && !changes['inventoryItem']) {
      return;
    }

    if (this.inventoryItem) {
      this.syncInventoryItemState(this.inventoryItem);
      return;
    }

    this.loadInventoryItem();
  }

  public onPreviewFieldUpdate(fieldName: InventoryItemDescriptionEditableFieldName): void {
    const currentItem = this.inventoryItem$.getValue();
    if (!currentItem) {
      return;
    }

    const rawValue = this.overviewForm.form.controls[fieldName].value ?? '';
    const normalizedValue = typeof rawValue === 'string' ? rawValue.trim() : '';
    const changes = this.withConcurrencyMetadata(currentItem, this.patchUpdaters[fieldName](currentItem, normalizedValue));

    this.inventoryItemService
      .updateInventoryItemGeneralInfo(this.companyId, this.itemId, changes)
      .pipe(
        first(),
        tap((inventoryItem) => {
          this.syncInventoryItemState(inventoryItem, this.getFieldLabelKey(fieldName));
          this.inventoryItemChange.emit(inventoryItem);
        }),
        catchError((error) => {
          this.syncInventoryItemState(currentItem);
          if (isConcurrencyConflictError(error)) {
            this.concurrencyConflict.emit();
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private loadInventoryItem(): void {
    if (!this.companyId || !this.itemId) {
      return;
    }

    this.isLoading = true;
    this.inventoryItemService
      .getInventoryItem(this.companyId, this.itemId)
      .pipe(
        first(),
        tap((inventoryItem) => this.syncInventoryItemState(inventoryItem)),
        catchError(() => EMPTY),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private syncInventoryItemState(
    inventoryItem: InventoryItem,
    previewFieldLabelKey?: string,
  ): void {
    const isEditable = inventoryItem.isArchived !== true;

    this.inventoryItem$.next(inventoryItem);
    this.overviewForm.form.patchValue(
      {
        shortDescription: inventoryItem.shortDescription ?? '',
        fullDescription: inventoryItem.fullDescription ?? '',
      },
      { emitEvent: false },
    );
    this.viewModel$.next({
      previewFieldLabelKey,
      fields: [
        {
          controlName: 'shortDescription',
          label: 'COMPONENTS.GOODS.FIELDS.SHORT_DESCRIPTION',
          value: inventoryItem.shortDescription ?? '',
          inputType: 'custom',
          editable: isEditable,
        },
        {
          controlName: 'fullDescription',
          label: 'COMPONENTS.GOODS.FIELDS.FULL_DESCRIPTION',
          value: inventoryItem.fullDescription ?? '',
          inputType: 'custom',
          editable: isEditable,
        },
      ],
    });
  }

  private getFieldLabelKey(fieldName: InventoryItemDescriptionEditableFieldName): string {
    const labels: Record<InventoryItemDescriptionEditableFieldName, string> = {
      shortDescription: 'COMPONENTS.GOODS.FIELDS.SHORT_DESCRIPTION',
      fullDescription: 'COMPONENTS.GOODS.FIELDS.FULL_DESCRIPTION',
    };

    return labels[fieldName];
  }

  private toOptionalString(value: string): string | undefined {
    return value ? value : undefined;
  }

  private withConcurrencyMetadata<T extends object>(
    inventoryItem: InventoryItem,
    changes: T,
  ): T & { expectedConcurrencyToken: string } {
    return {
      ...changes,
      expectedConcurrencyToken: inventoryItem.concurrencyToken ?? '',
    };
  }
}
